import { useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { Badge, Icon } from '../index';
import { webTapFloor } from '../theme';
import { useTranslation } from 'react-i18next';

import type { DraftSet, WeightUnit } from './state/loggerDraft';
import type { useTheme } from './theme';
import { numericFieldErrorText } from '@jackedlogs/shared';
import { steppedWeight } from './weightStepper';
import { useFieldFocusReporter } from './useFieldFocus';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';
import { SetRowStepperBar } from './SetRowStepperBar';
import { FocusPanelCardio } from './FocusPanelCardio';
import { FocusPanelTimed } from './FocusPanelTimed';
import { HeaderTooltip } from './HeaderTooltip';
import { amrapNeedsReps } from './amrapGate';
import { STEPS_KG, STEPS_LB } from './weightStepConstants';

// The DraftSet fields the input area writes through. Mirrors the strength
// inputs + the cardio duration/optionals (parity with CardioSetRow).
export type EditableSetField =
  | 'reps'
  | 'weight'
  | 'extraNegative'
  | 'rir'
  | 'rpe'
  | 'duration'
  | 'distanceM'
  | 'avgHrBpm'
  | 'calories'
  | 'dropset';

// FocusPanelInputs (timeline logger) — the reps/weight (or cardio) data-entry
// area of the FocusPanel, extracted to keep FocusPanel under the 500-line
// budget. It owns the focus state for the contextual stepper bar and writes
// through `onChange` on every keystroke, exactly as it did inline. Rendered
// only when a set is bound; the dropset/myorep case defers its rounds to the
// DropsetBlock in FocusPanelDetails (the inline cells hide here, mirroring
// the card logger). Effort left this row in AP-0b: the meter under the card
// (FocusPanelEffort) is the single effort write path now.
export function FocusPanelInputs({
  set: s,
  isBodyweight,
  isCardio,
  isTimeBased,
  defaultUnit,
  onChange,
  onOpenPlates,
  onToggleUnit,
  onWeightFocus,
  theme,
}: {
  set: DraftSet;
  isBodyweight: boolean;
  isCardio: boolean;
  /** entry.isTimeBased — isometric hold; shows a duration field only. */
  isTimeBased: boolean;
  defaultUnit: WeightUnit;
  onChange: (patch: Partial<Pick<DraftSet, EditableSetField>>) => void;
  onOpenPlates: () => void;
  onToggleUnit: () => void;
  /** Fired when the weight field gains focus — FocusPanel uses it for the
   *  once-per-exercise per-implement tip («enter ONE dumbbell's weight»).
   *  Kept as a callback so this component stays free of tip bookkeeping
   *  (useTransientTip lives once at FocusPanel level, via useHelpMode). */
  onWeightFocus?: () => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const [weightFocused, setWeightFocused] = useState(false);
  const [stepSign, setStepSign] = useState<1 | -1>(1);

  // Bodyweight extra: magnitude-only field, sign on extraNegative (same as
  // SetRow.onChangeWeight). A typed minus turns the field amber.
  const onChangeWeight = (text: string) => {
    if (!isBodyweight) {
      onChange({ weight: text });
      return;
    }
    const hasMinus = /[-−]/.test(text);
    const magnitude = text.replace(/[-−]/g, '').trim();
    if (hasMinus) onChange({ weight: magnitude, extraNegative: true });
    else if (magnitude === '') onChange({ weight: '', extraNegative: false });
    else onChange({ weight: magnitude });
  };
  const isAssist =
    isBodyweight && (s.extraNegative || /^\s*[-−]/.test(s.weight));
  const applyDelta = (delta: number) => {
    onChange({ weight: steppedWeight(s.weight, delta) });
  };

  // Tap a populated field → select the whole number so you type over it.
  const repsSel = useSelectAllOnFocus(s.reps);
  const weightSel = useSelectAllOnFocus(s.weight);
  // Tell the logger a field is holding the keyboard, so the rest-end resume
  // waits instead of pulling the caret out of a half-typed number (TM-18a).
  const fieldFocus = useFieldFocusReporter();

  // Inline numeric validation (2026-06-08), locale-aware; danger border on the
  // bad field + one message line below the input row.
  const repsErr = numericFieldErrorText('reps', s.reps, t);
  const weightErr = numericFieldErrorText('weight', s.weight, t);
  // A prescribed AMRAP starts with an empty reps cell (LG-28) and cannot be
  // completed until it is filled (useTimelineLogger.amrapNeedsReps). That rule
  // has to be READABLE, or the band tap just stops working for no visible
  // reason. It is not an error — nothing is wrong yet, the athlete simply has
  // not typed the result — so it renders as the row's cue and never turns the
  // field's border red. `validateNumericField` deliberately calls an empty
  // field valid (it must, or every field would flash mid-typing), so this
  // state cannot come from there and is computed here.
  const needsReps = amrapNeedsReps(s);
  const rowError = [repsErr, weightErr].filter(Boolean).join(' · ') || null;
  const rowCue =
    rowError === null && needsReps
      ? t('mobile.logger.amrapNeedsReps', {
          defaultValue: 'Type the reps you did to log this set.',
        })
      : null;

  return (
    <>
      <View style={styles.inputRow}>
        {isTimeBased ? (
          <FocusPanelTimed
            set={s}
            defaultUnit={defaultUnit}
            onChange={onChange}
            theme={theme}
          />
        ) : isCardio ? (
          <FocusPanelCardio
            set={s}
            onChange={onChange}
            theme={theme}
          />
        ) : s.dropset ? (
          // Dropset/myorep active: the chain rounds own input below (the
          // DropsetBlock in FocusPanelDetails). Hide the single reps/weight
          // row to avoid a second, conflicting parent input — mirrors the
          // card logger, where the type bar replaces the inline cells.
          <Text style={styles.hint}>
            {t('mobile.timeline.dropsetActive', {
              defaultValue: 'Dropset — edit the rounds below.',
            })}
          </Text>
        ) : (
          <>
            <View style={styles.field}>
              {/* A PRESCRIBED AMRAP used to live only in the placeholder, so it
                  vanished the moment the first digit was typed — exactly when
                  you still need to know the target is "at least N, then as many
                  as possible" (LG-23, inbox 3d88179b). The badge sits on the
                  label and STAYS. It is display-only on purpose: <Badge> is the
                  non-interactive status tag, never <Chip> — the AMRAP *toggle*
                  is a different control and stays hidden until the flag
                  persists ([[reference_amrap_via_reps_form]]). It reads
                  `amrapPrescribed` — the PLAN's request — never the
                  athlete's `amrap` outcome: the badge answers "what am I
                  being asked for?", the chip answers "what did I do?". */}
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>
                  {t('mobile.logger.setReps')}
                </Text>
                {s.amrapPrescribed ? (
                  <Badge
                    theme={theme}
                    // The floor rides on the badge, not only in the
                    // placeholder: a placeholder dies on the first keystroke,
                    // which is exactly when a two-digit answer needs checking
                    // against the target. The badge is permanent, so "AMRAP
                    // ≥ 8" is still readable while typing. Falls back to the
                    // bare word when there is no floor (a chip-marked AMRAP
                    // nobody prescribed).
                    label={
                      s.amrapFloor
                        ? t('mobile.logger.amrapBadgeWithFloor', {
                            defaultValue: 'AMRAP ≥ {{reps}}',
                            reps: s.amrapFloor,
                          })
                        : t('program.amrapBadge', { defaultValue: 'AMRAP' })
                    }
                    a11yLabel={
                      s.amrapFloor
                        ? t('mobile.logger.amrapPrescribedFloorA11y', {
                            defaultValue:
                              'AMRAP: at least {{reps}} reps, then as many as possible',
                            reps: s.amrapFloor,
                          })
                        : t('mobile.logger.amrapPrescribedA11y', {
                            defaultValue:
                              'AMRAP: at least the target reps, then as many as possible',
                          })
                    }
                  />
                ) : null}
              </View>
              <TextInput
                ref={repsSel.ref}
                style={[styles.input, repsErr && styles.inputInvalid]}
                keyboardType="number-pad"
                inputMode="numeric"
                // On a prescribed AMRAP the cell arrives EMPTY (LG-28) and the
                // floor lives here instead, as "≥ 8". A placeholder cannot be
                // submitted by doing nothing, which is the whole point: the
                // prefilled floor was being read as the answer and confirmed
                // as a limit. The badge above says AMRAP, this says how far in
                // the number has to start — together they read "at least 8,
                // then as many as you can". Every other set keeps the bare
                // dash: its cell is already seeded with the prescribed reps.
                placeholder={
                  s.amrapPrescribed && s.amrapFloor
                    ? t('mobile.logger.amrapFloorPlaceholder', {
                        defaultValue: '≥ {{reps}}',
                        reps: s.amrapFloor,
                      })
                    : '—'
                }
                placeholderTextColor={theme.palette.ink3}
                value={s.reps}
                onChangeText={(reps) => onChange({ reps })}
                selectTextOnFocus
                onFocus={() => {
                  repsSel.onFocus();
                  fieldFocus.onFocus('reps');
                }}
                onBlur={() => fieldFocus.onBlur('reps')}
                maxLength={4}
              />
            </View>
            <View style={styles.field}>
              {/* Weight label carries the SAME tap-tip as the card logger's
                  weight column (HeaderTooltip, slice 5b) — explains kg/lb +
                  the ± assist convention for bodyweight rows. Reuses the
                  existing setWeightTip / setExtraTip keys; respects the Hints
                  toggle (off ⇒ plain label). Left-aligned to match the reps /
                  effort labels via the cellStyle override. */}
              {/* «2×» on the label when the set counts both sides (UNI /
                  two-implement): the convention — enter ONE implement's
                  weight — is readable right where the number is typed,
                  at zero layout cost (IB-8 family). Words-only a11yLabel:
                  the raw × glyph is never vocalised (audit 2026-07-14);
                  numberOfLines pins the row rhythm on wide locales (ФУНТ). */}
              <HeaderTooltip
                label={`${s.loadedSides === 2 ? '2× ' : ''}${
                  isBodyweight
                    ? t('mobile.logger.setExtra')
                    : defaultUnit === 'lb'
                      ? t('mobile.logger.setWeightLb', { defaultValue: 'LB' })
                      : t('mobile.logger.setWeight')
                }`}
                a11yLabel={
                  s.loadedSides === 2
                    ? t('mobile.logger.setWeightBothSidesA11y', {
                        unit: s.unit,
                        defaultValue:
                          'Weight of one side, both sides counted ({{unit}})',
                      })
                    : undefined
                }
                labelNumberOfLines={1}
                tip={
                  isBodyweight
                    ? t('mobile.logger.setExtraTip', {
                        defaultValue:
                          'Bodyweight assistance or added load. Type a minus (−) for band/machine assist (the field turns amber); a belt/plate add is positive.',
                      })
                    : t('mobile.logger.setWeightTip', {
                        defaultValue:
                          'Weight lifted per set. Switch kg/lb with the unit chip on each row.',
                      })
                }
                labelStyle={styles.fieldLabel}
                cellStyle={styles.labelCell}
                theme={theme}
              />
              <TextInput
                ref={weightSel.ref}
                style={[
                  styles.input,
                  isAssist && styles.inputNegative,
                  weightErr && styles.inputInvalid,
                ]}
                keyboardType="decimal-pad"
                inputMode="decimal"
                placeholder="—"
                placeholderTextColor={theme.palette.ink3}
                value={s.weight}
                onChangeText={onChangeWeight}
                selectTextOnFocus
                onFocus={() => {
                  setWeightFocused(true);
                  weightSel.onFocus();
                  fieldFocus.onFocus('weight');
                  onWeightFocus?.();
                }}
                onBlur={() => {
                  setWeightFocused(false);
                  fieldFocus.onBlur('weight');
                }}
                maxLength={6}
              />
            </View>
            {/* Empty third cell: keeps reps + weight each a 2-unit cell of the
                6-unit card grid, aligned with the rows below. The effort value
                moved to the meter (FocusPanelEffort, AP-0b). */}
            <View style={styles.field} />
          </>
        )}
      </View>

      {rowError ? (
        <View style={styles.fieldError} accessibilityRole="alert">
          <Icon name="warn" size={theme.typography.sizes.xs} color={theme.palette.danger} />
          <Text style={styles.fieldErrorText}>{rowError}</Text>
        </View>
      ) : rowCue ? (
        // Same slot, different voice: no warn glyph and no danger tint, because
        // an unfilled AMRAP is the normal state of a set you have not done yet.
        // It carries a text sentence, so it never depends on colour.
        <View style={styles.fieldError}>
          <Text style={styles.fieldCueText}>{rowCue}</Text>
        </View>
      ) : null}

      {weightFocused && !isCardio && !isTimeBased && !s.dropset ? (
        <SetRowStepperBar
          unit={s.unit}
          onOpenPlates={isBodyweight ? undefined : onOpenPlates}
          onToggleUnit={onToggleUnit}
          stepSign={stepSign}
          setStepSign={setStepSign}
          stepMagnitudes={s.unit === 'lb' ? STEPS_LB : STEPS_KG}
          applyDelta={applyDelta}
          theme={theme}
        />
      ) : null}
    </>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Card grid: 3 equal cells (reps · weight · effort) so each is a 2-unit
    // span of the 6-unit grid the set-count + controls rows share. gap s2 +
    // 36-tall boxes match those rows for one consistent rhythm (Antenore
    // 2026-07-02: the value fields looked bigger than the 36 control rows).
    inputRow: {
      flexDirection: 'row',
      gap: s.s2,
      alignItems: 'flex-end',
    },
    field: { flex: 1, gap: 4 },
    // Label + the AMRAP badge on one line. The cell is a third of the row, so
    // the badge wraps under the label rather than squeezing it on a narrow
    // screen (280dp) — the label must stay readable, the badge can move.
    // minHeight matches the weight column's HeaderTooltip cell (DS-51: its
    // interactive Pressable floors to webTapFloor on web). This label has no
    // Pressable of its own — nothing here needs the floor — but `inputRow`
    // bottom-aligns both columns (`alignItems:'flex-end'`), so without a
    // matching height the reps label would ride below the taller weight
    // label instead of sitting level with it. `alignContent` (not just
    // `alignItems`) is needed too — with `flexWrap` set, react-native-web
    // packs the single wrapped line via `alignContent` (default `stretch`)
    // BEFORE `alignItems` centers items within that line; without it the
    // 12px label sat flush at the row's top instead of centered in the
    // 44px box, an offset the empty AMRAP-badge case never exposed because
    // this row was never taller than its content until this fix (verified
    // headless: 16px offset without `alignContent`, 0px with it).
    fieldLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignContent: 'center',
      flexWrap: 'wrap',
      gap: 4,
      minHeight: webTapFloor,
    },
    // HeaderTooltip cell for the weight label: left-aligned to match the
    // plain reps/effort labels (overrides HeaderTooltip's default centering).
    labelCell: { alignItems: 'flex-start' },
    fieldLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    input: {
      height: 36,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: 0,
      color: p.ink,
      fontSize: ty.sizes.md,
      textAlign: 'center',
      backgroundColor: p.bg,
    },
    inputNegative: {
      borderColor: p.warn,
      color: p.warn,
    },
    // Inline validation: danger border + a message line below the inputs (never
    // colour alone — reduced-vision a11y).
    inputInvalid: {
      borderColor: p.danger,
      color: p.danger,
    },
    fieldError: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    fieldErrorText: {
      color: p.danger,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    // The AMRAP "type your reps" cue. `ink2`, not `danger`: it says what to do
    // next, not what went wrong. One tier up from the error text in size (sm,
    // not xs) because it is read standing at a machine and it is the only
    // thing standing between the athlete and a set that will not log.
    fieldCueText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
    hint: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
    },
  });
}
